import React, { useCallback, useEffect, useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useLocalStorage from '@afosto/utils-react/useLocalStorage';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import useSubscriptions from '@afosto/utils-react/subscription/useSubscriptions';
import { useConfirm } from '@afosto/app-components-react/ConfirmDialog';
import { useSnackbar } from 'notistack';
import { Link, Route, Switch, useHistory, useLocation, useParams } from 'react-router-dom';
import Page from '@afosto/app-components-react/Page';
import SkeletonPage from '@afosto/app-components-react/SkeletonPage';
import ParcelProvider from '../../components/ParcelProvider';
import printParcelTicket from '../../utils/printParcelTicket';
import ParcelDetails from './ParcelDetails';
import ParcelItems from './ParcelItems';
import ParcelLogs from './ParcelLogs';
import printAttachmentsJob from '@afosto/utils-react/spooler/printAttachmentsJob';
import PrintingFailedDialog from '@afosto/app-components-react/PrintingFailedDialog';
import ProgressDialog from '@afosto/app-components-react/ProgressDialog';
import { shipments as shipmentsApi } from '@afosto/fulfilment/api/Shipments';
import { contacts as contactsApi } from '@afosto/contacts/api/Contacts';
import { parcels as parcelsApi } from '@afosto/fulfilment/api/Parcels';
import canRequestShippingLabels from '../../utils/canRequestShippingLabels';
import fetchShipmentAddressing from '../../utils/fetchShipmentAddressing';
import fetchShippingMethod from '../../utils/fetchShippingMethod';
import scheduleTask from '@afosto/utils-react/tasks/scheduleTask';
import {
  HUB_SETTING_KEY,
  PARCEL_LABEL_HUB_SETTING_KEY,
  PARCEL_LABEL_PRINTER_SETTING_KEY,
  PRINTER_SETTING_KEY,
} from '../../utils/constants';
import translations from './translations';

const ViewParcel = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions([
    'lcs:shipments:read',
    'lcs:handling:read',
    'lcs:locations:read',
    'rel:contacts:read',
  ]);

  const confirm = useConfirm();
  const history = useHistory();
  const intl = useIntl();
  const location = useLocation();
  const { parcelID } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const subscriptions = useSubscriptions();
  const [getLocalStorage] = useLocalStorage();
  const [shipment, setShipment] = useState(null);
  const [parcel, setParcel] = useState(null);
  const [isFetchingParcel, setIsFetchingParcel] = useState(true);
  const [isFetchingAddress, setIsFetchingAddress] = useState(true);
  const [resolvedAddresses, setResolvedAddresses] = useState(null);
  const [contact, setContact] = useState(null);
  const [isFetchingContact, setIsFetchingContact] = useState(true);
  const [showPrintingProgressDialog, setShowPrintingProgressDialog] = useState(false);
  const [showPrintingFailedDialog, setShowPrintingFailedDialog] = useState(false);
  const [showFetchingProgressDialog, setShowFetchingProgressDialog] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [shippingMethod, setShippingMethod] = useState(null);

  const fetchParcel = useCallback(async () => {
    try {
      const parcelResponse = await parcelsApi.getParcel(parcelID);
      const { data: parcelData } = parcelResponse;

      setParcel(parcelData);
      return parcelData;
    } catch (err) {
      setParcel(null);
      return Promise.reject(err);
    }
  }, [parcelID]);

  useEffect(() => {
    let isMounted = true;

    const fetchParcelData = async () => {
      setIsFetchingParcel(true);

      try {
        const parcelData = await fetchParcel();
        const shipmentResponse = await shipmentsApi.viewShipment(parcelData?.shipment_id);
        const { data: shipmentData } = shipmentResponse;
        let shippingMethodData = null;

        if (shipmentData?.method_id) {
          shippingMethodData = await fetchShippingMethod(shipmentData.method_id).catch(() => null);
        }

        setShippingMethod(shippingMethodData);

        await fetchParcel();
        setShipment(shipmentData);
      } catch (err) {
        if (isMounted) {
          setShipment(null);
        }
      } finally {
        if (isMounted) {
          setIsFetchingParcel(false);
        }
      }
    };

    fetchParcelData();

    return () => {
      isMounted = false;
    };
  }, [parcelID, fetchParcel]);

  const handleDeleteParcel = async () => {
    try {
      const confirmResponse = await confirm({
        title: intl.formatMessage(translations.confirmDeleteParcel.title),
        description: intl.formatMessage(translations.confirmDeleteParcel.description),
        catchOnCancel: false,
        buttons: [
          {
            label: intl.formatMessage(translations.confirmDeleteParcel.buttons.cancel),
            type: 'cancel',
            variant: 'outlined',
          },
          {
            label: intl.formatMessage(translations.confirmDeleteParcel.buttons.delete),
            response: 'delete',
            type: 'submit',
            variant: 'contained',
            color: 'primary',
          },
        ],
      });

      if (confirmResponse === 'delete') {
        await parcelsApi.removeParcel(parcel.id);

        enqueueSnackbar(intl.formatMessage(translations.feedback.parcelDeleted), {
          variant: 'success',
        });

        history.push('/parcels');
      }
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.parcelNotDeleted), {
        variant: 'error',
      });
    }
  };

  const handleEditParcelItems = () => {
    history.push(`/packing/${parcel?.shipment_id}/${parcel.id}`);
  };

  /**
   * Print the shipping label of the parcel
   */
  const handlePrintShippingLabel = async (download = false) => {
    try {
      if (!shipment?.method_id) {
        enqueueSnackbar(intl.formatMessage(translations.feedback.noShippingMethod), {
          variant: 'error',
        });
        return;
      }

      const hasSpoolerSubscription = subscriptions.some(({ code: appCode }) => appCode === 'spl');

      setActionType('label');
      setShowPrintingFailedDialog(false);
      setShowFetchingProgressDialog(true);

      const requestResponse = await parcelsApi.requestLabel(parcel?.id);
      const { headers = {} } = requestResponse;
      const { 'x-task-id': taskID = null } = headers;
      const { result } = await scheduleTask(taskID);
      const file = result ? JSON.parse(result) : null;

      if (!file) {
        throw new Error('No file found!');
      }

      setShowFetchingProgressDialog(false);

      if (hasSpoolerSubscription && !download) {
        setShowPrintingProgressDialog(true);

        const hubID = getLocalStorage(PARCEL_LABEL_HUB_SETTING_KEY);
        const printerID = getLocalStorage(PARCEL_LABEL_PRINTER_SETTING_KEY);
        const isConfigured = printerID && printerID !== '' && hubID && hubID !== '';

        if (isConfigured) {
          if (file.url) {
            const description = `Printing shipping label for parcel: ${parcel?.id}`;
            await printAttachmentsJob([{ url: file.url }], description, {
              printerID,
              hubID,
            });
          }
        }
      } else {
        // TODO: print without spooler
        window.open(file.url, '_blank');
      }

      setActionType(null);
    } catch (error) {
      setShowPrintingFailedDialog(true);
      setShowFetchingProgressDialog(false);
    } finally {
      setShowPrintingProgressDialog(false);
    }
  };

  const handlePrintShippingSlip = async (download = false) => {
    try {
      const hasSpoolerSubscription = subscriptions.some(({ code: appCode }) => appCode === 'spl');

      setActionType('slip');
      setShowPrintingFailedDialog(false);
      setShowFetchingProgressDialog(true);

      const requestResponse = await parcelsApi.requestSlip(parcel?.id);
      const { headers = {} } = requestResponse;
      const { 'x-task-id': taskID = null } = headers;
      const { result } = await scheduleTask(taskID);
      const file = result ? JSON.parse(result) : null;

      if (!file) {
        throw new Error('No file found!');
      }

      setShowFetchingProgressDialog(false);

      if (hasSpoolerSubscription && !download) {
        setShowPrintingProgressDialog(true);

        const hubID = getLocalStorage(HUB_SETTING_KEY);
        const printerID = getLocalStorage(PRINTER_SETTING_KEY);
        const isConfigured = printerID && printerID !== '' && hubID && hubID !== '';

        if (isConfigured && file?.url) {
          const description = `Printing shipping slip for parcel: ${parcel?.id}`;
          await printAttachmentsJob([{ url: file.url }], description, {
            printerID,
            hubID,
          });
        }
      } else {
        // TODO: print without spooler
        window.open(file.url, '_blank');
      }

      setActionType(null);
    } catch (error) {
      setShowPrintingFailedDialog(true);
      setShowFetchingProgressDialog(false);
    } finally {
      setShowPrintingProgressDialog(false);
    }
  };

  const handlePrintParcelTicket = async () => {
    try {
      const hasSpoolerSubscription = subscriptions.some(({ code: appCode }) => appCode === 'spl');

      setActionType('ticket');
      setShowPrintingFailedDialog(false);

      if (hasSpoolerSubscription) {
        const hubID = getLocalStorage(PARCEL_LABEL_HUB_SETTING_KEY);
        const printerID = getLocalStorage(PARCEL_LABEL_PRINTER_SETTING_KEY);
        const isConfigured = printerID && printerID !== '' && hubID && hubID !== '';

        if (isConfigured) {
          setShowPrintingProgressDialog(true);

          await printParcelTicket(intl, parcel, { hubID, printerID, shouldPoll: true });
        }
      }
    } catch (err) {
      setShowPrintingFailedDialog(true);
    } finally {
      setShowPrintingProgressDialog(false);
    }
  };

  const handleMarkInTransit = async () => {
    try {
      await parcelsApi.setParcelInTransit(parcel.id);

      if (parcel?.position) {
        await parcelsApi.updateParcel(parcel.id, {
          weight: parcel?.weight,
          length: parcel?.length,
          width: parcel?.width,
          height: parcel?.height,
          tracking_code: parcel?.tracking_code,
          tracking_url: parcel?.tracking_url,
          ship_at: parcel?.ship_at,
          position: null,
        });
      }

      setParcel({ ...parcel, is_in_transit: true, in_transit_at: new Date(), position: null });

      enqueueSnackbar(intl.formatMessage(translations.feedback.parcelMarkedInTransit), {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.parcelNotMarkedInTransit), {
        variant: 'error',
      });
    }
  };

  const handleMarkFinished = async () => {
    try {
      await parcelsApi.setParcelFinished(parcel.id);

      if (parcel?.position) {
        await parcelsApi.updateParcel(parcel.id, {
          weight: parcel?.weight,
          length: parcel?.length,
          width: parcel?.width,
          height: parcel?.height,
          tracking_code: parcel?.tracking_code,
          tracking_url: parcel?.tracking_url,
          ship_at: parcel?.ship_at,
          position: null,
        });
      }

      setParcel({ ...parcel, is_finished: true, finished_at: new Date(), position: null });

      enqueueSnackbar(intl.formatMessage(translations.feedback.parcelMarkedFinished), {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.parcelNotMarkedFinished), {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    /**
     * Fetch addressing
     * @returns {Promise<Array>}
     */
    const fetchAddressing = async () => {
      setIsFetchingAddress(true);

      try {
        const addressingData = await fetchShipmentAddressing(shipment);
        setResolvedAddresses(addressingData);
      } catch (error) {
        setResolvedAddresses({});
      } finally {
        setIsFetchingAddress(false);
      }
    };

    /**
     * Fetch the contact linked to the shipment
     * @param contactId
     * @returns {Promise}
     */
    const fetchContact = async contactId => {
      setIsFetchingContact(true);

      try {
        const response = await contactsApi.getContact(contactId);
        const { data = {} } = response;

        setContact(data);
        return data;
      } catch (error) {
        return Promise.reject(error);
      } finally {
        setIsFetchingContact(false);
      }
    };

    if (shipment) {
      const { contact_id: contactId } = shipment;
      fetchAddressing();
      fetchContact(contactId);
    }
  }, [shipment]);

  if (!hasPermissions()) {
    return (
      <Page title={(parcel && parcel.number) || (parcel && parcel.id)} fullWidth>
        <UnauthorizedComponent />
      </Page>
    );
  }

  if (isFetchingParcel) {
    return <SkeletonPage fullWidth breadcrumbs={4} />;
  }

  const secondaryActions = [
    ...(parcel && !parcel.is_in_transit && !parcel.is_finished && canRequestShippingLabels(shipment)
      ? [
          {
            text: intl.formatMessage(translations.secondaryActions.printLabel),
            action: handlePrintShippingLabel,
          },
        ]
      : []),
    ...(parcel && !parcel.is_finished
      ? [
          {
            text: intl.formatMessage(translations.secondaryActions.markFinished),
            action: handleMarkFinished,
          },
        ]
      : []),
    ...(parcel && hasPermissions(['lcs:handling:write'])
      ? [
          {
            text: intl.formatMessage(translations.secondaryActions.printSlip),
            action: handlePrintShippingSlip,
          },
        ]
      : []),
    ...(parcel && !parcel.is_in_transit && !parcel.is_finished
      ? [
          {
            text: intl.formatMessage(translations.secondaryActions.markInTransit),
            action: handleMarkInTransit,
          },
          ...(hasPermissions(['lcs:handling:write'])
            ? [
                {
                  text: intl.formatMessage(translations.secondaryActions.printTicket),
                  action: handlePrintParcelTicket,
                },
                {
                  text: intl.formatMessage(translations.secondaryActions.editParcelItems),
                  action: handleEditParcelItems,
                },
                {
                  text: intl.formatMessage(translations.secondaryActions.deleteParcel),
                  action: handleDeleteParcel,
                  color: 'secondary',
                },
              ]
            : []),
        ]
      : []),
  ];

  return (
    <ParcelProvider
      value={{
        parcel,
        setParcel,
        isFetchingParcel,
        shipment,
        addresses: resolvedAddresses,
        isFetchingAddress,
        contact,
        isFetchingContact,
        fetchParcel,
        deleteParcel: handleDeleteParcel,
        shippingMethod,
      }}
    >
      <Page
        title={(parcel && parcel.number) || (parcel && parcel.id)}
        subNavigation={{
          current: location.pathname,
          tabs: [
            {
              label: intl.formatMessage(translations.subNavigation.details),
              path: `/parcels/${parcelID}`,
            },
            {
              label: intl.formatMessage(translations.subNavigation.items),
              path: `/parcels/${parcelID}/items`,
            },
            {
              label: intl.formatMessage(translations.subNavigation.logs),
              path: `/parcels/${parcelID}/logs`,
            },
          ],
          onChange: (e, value) => history.replace(value),
        }}
        breadcrumbs={[
          {
            label: intl.formatMessage(translations.breadcrumb.parcels),
            path: '/parcels',
          },
          {
            label: (parcel && parcel.number) || (parcel && parcel.id),
            path: `/parcels/${parcelID}`,
            active: true,
          },
        ]}
        linkComponent={Link}
        useBreadcrumbNavigation={false}
        secondaryActions={secondaryActions.length > 0 ? secondaryActions : null}
        fullWidth
      >
        <Switch>
          <Route component={ParcelLogs} path="/parcels/:parcelID/logs" />
          <Route component={ParcelItems} path="/parcels/:parcelID/items" />
          <Route component={ParcelDetails} path="/parcels/:parcelID" />
        </Switch>
      </Page>
      <ProgressDialog
        message={
          actionType ? intl.formatMessage(translations.progressDialog[actionType].fetching) : ''
        }
        isOpen={showFetchingProgressDialog}
      />
      <ProgressDialog
        message={
          actionType ? intl.formatMessage(translations.progressDialog[actionType].printing) : ''
        }
        isOpen={showPrintingProgressDialog}
      />
      <PrintingFailedDialog
        message={
          actionType
            ? intl.formatMessage(translations.printingFailedDialog[actionType].message)
            : ''
        }
        title={
          actionType ? intl.formatMessage(translations.printingFailedDialog[actionType].title) : ''
        }
        isOpen={showPrintingFailedDialog}
        onClose={() => setShowPrintingFailedDialog(false)}
        onRetry={() => handlePrintShippingLabel()}
        onDownload={() => handlePrintShippingLabel(true)}
      />
    </ParcelProvider>
  );
};

export default ViewParcel;
